// src/components/Account/TransactionDetails.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  AppBar,
  Toolbar,
  Drawer,
  List as MuiList,
  ListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Collapse,
  CssBaseline,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  ReceiptLong as ReceiptLongIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  LocalOffer as DiscountIcon,
  CardGiftcard as ComplimentaryIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  TableRestaurant as TableRestaurantIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  History as HistoryIcon,
  Note as NoteIcon,
  ListAlt as ListAltIcon,
  Dashboard as DashboardIcon,
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Receipt as ReceiptOutlinedIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Storefront as StorefrontIcon,
  TableChart as TableChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  PointOfSale as PointOfSaleIcon,
  QrCode as QrCodeIcon,
  PhoneAndroid as PhoneAndroidIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Header from '../../../components/Account/Header';
import Footer from '../../../components/Account/Footer';
import Sidebar from '../../../components/Account/Sidebar';
import Preloader from '../../../theme/Preloader';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// Location Configuration
const LOCATION_CONFIG = {
  african: { id: 5, name: 'African', label: 'African', color: '#2E7D32', icon: '🌍' },
  continental: { id: 3, name: 'Continental', label: 'Continental', color: '#1565C0', icon: '🌐' },
  lounge: { id: 4, name: 'Lounge', label: 'Lounge', color: '#6A1B9A', icon: '🍸' },
  all: { id: null, name: 'All Locations', label: 'All Locations', color: '#1976D2', icon: '📊' },
};

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  // { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const ORDER_STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'READY', label: 'Ready' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All Payment' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'COMPLIMENTARY', label: 'Complimentary' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const PAYMENT_METHOD_COLORS = {
  'Cash': '#4CAF50',
  'Card': '#1976D2',
  'Bank Transfer': '#9C27B0',
  'Mobile Money': '#FF6F00',
  'Complimentary': '#FF9800',
  'Mixed': '#E65100',
  'Unknown': '#9E9E9E',
};

const PAYMENT_METHOD_ICONS = {
  'Cash': <MoneyIcon fontSize="small" />,
  'Card': <CreditCardIcon fontSize="small" />,
  'Bank Transfer': <BankIcon fontSize="small" />,
  'Mobile Money': <PhoneAndroidIcon fontSize="small" />,
  'Complimentary': <ComplimentaryIcon fontSize="small" />,
  'Mixed': <PointOfSaleIcon fontSize="small" />,
  'Unknown': <QrCodeIcon fontSize="small" />,
};

const STATUS_COLORS = {
  'PENDING': { bg: '#FFF3E0', color: '#E65100', label: 'Pending' },
  'IN_PROGRESS': { bg: '#E3F2FD', color: '#0D47A1', label: 'In Progress' },
  'READY': { bg: '#E8F5E9', color: '#1B5E20', label: 'Ready' },
  'COMPLETED': { bg: '#F3E5F5', color: '#4A148C', label: 'Completed' },
  'CANCELLED': { bg: '#FFEBEE', color: '#B71C1C', label: 'Cancelled' },
  'UNPAID': { bg: '#FFEBEE', color: '#C62828', label: 'Unpaid' },
  'PAID': { bg: '#E8F5E9', color: '#2E7D32', label: 'Paid' },
  'PARTIAL': { bg: '#FFF3E0', color: '#E65100', label: 'Partial' },
  'REFUNDED': { bg: '#F3E5F5', color: '#4A148C', label: 'Refunded' },
  'COMPLIMENTARY': { bg: '#F3E5F5', color: '#7B1FA2', label: 'Complimentary' },
};

// ============================================
// STYLED COMPONENTS
// ============================================

const drawerWidth = 260;

const MainContent = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      marginLeft: 0,
    },
  })
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2, 0),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
  [theme.breakpoints.down('sm')]: {
    '&::before': {
      left: '16px',
    },
  },
}));

const TimelineItemContainer = styled(Box)(({ theme, isLast }) => ({
  display: 'flex',
  position: 'relative',
  marginBottom: isLast ? 0 : theme.spacing(3),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const TimelineDotWrapper = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: 44,
  flexShrink: 0,
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    width: 32,
  },
}));

const TimelineDot = styled(Box)(({ theme, color }) => ({
  width: 44,
  height: 44,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: color || theme.palette.primary.main,
  color: theme.palette.common.white,
  boxShadow: `0 4px 12px ${alpha(color || theme.palette.primary.main, 0.3)}`,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: 32,
    height: 32,
    '& svg': {
      fontSize: '1rem',
    },
  },
}));

const TimelineConnector = styled(Box)(({ theme, isLast }) => ({
  width: 2,
  flex: 1,
  backgroundColor: alpha(theme.palette.primary.main, 0.15),
  minHeight: 20,
  marginTop: 4,
  display: isLast ? 'none' : 'block',
  [theme.breakpoints.down('sm')]: {
    marginLeft: 16,
  },
}));

const TimelineContentWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(2.5),
  paddingTop: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1.5),
  },
}));

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (amount === null || amount === undefined) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'N/A';
  }
};

const getStatusColor = (status) => {
  return STATUS_COLORS[status] || { bg: '#F5F5F5', color: '#616161', label: status || 'Unknown' };
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getPaymentMethodIcon = (method) => {
  return PAYMENT_METHOD_ICONS[method] || PAYMENT_METHOD_ICONS['Unknown'];
};

const getPaymentMethodColor = (method) => {
  return PAYMENT_METHOD_COLORS[method] || PAYMENT_METHOD_COLORS['Unknown'];
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = parts[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[TransactionDetails] Error checking token expiry:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[TransactionDetails] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[TransactionDetails] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[TransactionDetails] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[TransactionDetails] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/account_LoginPage';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[TransactionDetails] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;
  
  if (options.params) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  console.debug(`[TransactionDetails] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { 
      ...options, 
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 401) {
      console.debug('[TransactionDetails] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[TransactionDetails] API Error:', err.message);
    throw err;
  }
};

// ============================================
// CHILD COMPONENT: Sales Summary Tab - FIXED
// ============================================

const SalesSummaryTab = memo(({ transactions, summary, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate payment method breakdown from transactions
  // MOVED this BEFORE the early return so useMemo is always called
  const paymentBreakdown = useMemo(() => {
    const breakdown = {
      Cash: { count: 0, total: 0, transactions: [] },
      Card: { count: 0, total: 0, transactions: [] },
      'Bank Transfer': { count: 0, total: 0, transactions: [] },
      'Mobile Money': { count: 0, total: 0, transactions: [] },
      Complimentary: { count: 0, total: 0, transactions: [] },
      Mixed: { count: 0, total: 0, transactions: [] },
      Unpaid: { count: 0, total: 0, transactions: [] },
    };

    let totalRevenue = 0;
    let totalOrders = 0;
    let totalDiscounts = 0;
    let totalComplimentary = 0;

    transactions.forEach(tx => {
      // Skip cancelled orders
      if (tx.order_status === 'CANCELLED' || tx.is_canceled) return;

      totalOrders++;

      // Check if complimentary
      if (tx.is_complimentary || tx.payment_status === 'COMPLIMENTARY') {
        breakdown.Complimentary.count++;
        breakdown.Complimentary.total += tx.total_amount || 0;
        breakdown.Complimentary.transactions.push(tx);
        totalComplimentary += tx.total_amount || 0;
        return;
      }

      // Check if unpaid
      if (tx.payment_status === 'UNPAID') {
        breakdown.Unpaid.count++;
        breakdown.Unpaid.total += tx.total_amount || 0;
        breakdown.Unpaid.transactions.push(tx);
        return;
      }

      // Track discounts
      if (tx.discount && tx.discount > 0) {
        totalDiscounts += tx.discount;
      }

      // Determine payment method(s)
      const paymentMethods = tx.payments?.map(p => p.payment_method) || [];
      const uniqueMethods = [...new Set(paymentMethods)];

      // Mixed payment detection
      if (uniqueMethods.length > 1) {
        breakdown.Mixed.count++;
        breakdown.Mixed.total += tx.total_paid || 0;
        breakdown.Mixed.transactions.push(tx);
        totalRevenue += tx.total_paid || 0;
        return;
      }

      // Single payment method
      const method = uniqueMethods[0] || 'Unknown';
      if (breakdown[method]) {
        breakdown[method].count++;
        breakdown[method].total += tx.total_paid || 0;
        breakdown[method].transactions.push(tx);
        totalRevenue += tx.total_paid || 0;
      } else {
        // Unknown method - add to Cash as fallback
        breakdown.Cash.count++;
        breakdown.Cash.total += tx.total_paid || 0;
        breakdown.Cash.transactions.push(tx);
        totalRevenue += tx.total_paid || 0;
      }
    });

    // Calculate percentages
    const totalPaid = totalRevenue;
    Object.keys(breakdown).forEach(key => {
      if (breakdown[key].total > 0) {
        breakdown[key].percentage = (breakdown[key].total / totalPaid) * 100;
      } else {
        breakdown[key].percentage = 0;
      }
    });

    return {
      breakdown,
      totalRevenue,
      totalOrders,
      totalDiscounts,
      totalComplimentary,
      totalPaid,
    };
  }, [transactions]);

  // NOW we can do the early return - AFTER all hooks
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: '12px' }} />
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '12px' }} />
      </Box>
    );
  }

  const { breakdown, totalRevenue, totalOrders, totalDiscounts, totalComplimentary } = paymentBreakdown;

  // Payment method summary cards
  const paymentCards = [
    { key: 'Cash', label: 'Cash', icon: <MoneyIcon />, color: '#4CAF50' },
    { key: 'Card', label: 'Card (POS)', icon: <CreditCardIcon />, color: '#1976D2' },
    { key: 'Bank Transfer', label: 'Bank Transfer', icon: <BankIcon />, color: '#9C27B0' },
    { key: 'Mobile Money', label: 'Mobile Money', icon: <PhoneAndroidIcon />, color: '#FF6F00' },
    { key: 'Mixed', label: 'Mixed Payments', icon: <PointOfSaleIcon />, color: '#E65100' },
    { key: 'Complimentary', label: 'Complimentary', icon: <ComplimentaryIcon />, color: '#FF9800' },
    { key: 'Unpaid', label: 'Unpaid', icon: <WarningAmberIcon />, color: '#C62828' },
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#2E7D32', 0.06), borderRadius: '12px' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Revenue</Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#2E7D32' }}>
              {formatCurrency(totalRevenue)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#1976D2', 0.06), borderRadius: '12px' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Orders</Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#1976D2' }}>
              {totalOrders}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#FF9800', 0.06), borderRadius: '12px' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Discounts</Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#FF9800' }}>
              {formatCurrency(totalDiscounts)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#9C27B0', 0.06), borderRadius: '12px' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Complimentary</Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#9C27B0' }}>
              {formatCurrency(totalComplimentary)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Method Breakdown Cards */}
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
        💳 Payment Method Breakdown
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {paymentCards.map((card) => {
          const data = breakdown[card.key];
          const total = data?.total || 0;
          const count = data?.count || 0;
          const percentage = data?.percentage || 0;

          return (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: `2px solid ${alpha(card.color, 0.15)}`,
                  bgcolor: alpha(card.color, 0.05),
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(card.color, 0.15), color: card.color }}>
                    {card.icon}
                  </Avatar>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    {card.label}
                  </Typography>
                  {percentage > 0 && (
                    <Chip
                      label={`${percentage.toFixed(1)}%`}
                      size="small"
                      sx={{ ml: 'auto', bgcolor: alpha(card.color, 0.12), color: card.color, fontWeight: 700, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, color: card.color }}>
                    {formatCurrency(total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {count} order{count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                {total > 0 && (
                  <Box sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: alpha(card.color, 0.15) }}>
                    <Box sx={{ height: '100%', borderRadius: 2, bgcolor: card.color, width: `${Math.min(percentage, 100)}%` }} />
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Detailed Payment Method Table */}
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
        📋 Detailed Payment Breakdown
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: '12px', border: `2px solid ${alpha('#000', 0.06)}` }}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha('#1976D2', 0.06) }}>
              <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Payment Method</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Orders</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Total Amount</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Percentage</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Distribution</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentCards.map((card) => {
              const data = breakdown[card.key];
              const total = data?.total || 0;
              const count = data?.count || 0;
              const percentage = data?.percentage || 0;

              if (total === 0 && count === 0) return null;

              return (
                <TableRow key={card.key} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(card.color, 0.12), color: card.color }}>
                        {card.icon}
                      </Avatar>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                        {card.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    {count}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.85rem' }, color: card.color }}>
                    {formatCurrency(total)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    {percentage.toFixed(1)}%
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ width: 100, height: 8, borderRadius: 4, bgcolor: alpha('#000', 0.08), mx: 'auto' }}>
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: 4,
                          bgcolor: card.color,
                          width: `${Math.min(percentage, 100)}%`,
                          transition: 'width 0.5s ease',
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
});

SalesSummaryTab.displayName = 'SalesSummaryTab';

// ============================================
// CHILD COMPONENT: Custom Timeline
// ============================================

const CustomTimeline = memo(({ events, isLoading }) => {
  const theme = useTheme();
  
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: '8px' }} />
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: '8px' }} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px' }} />
      </Box>
    );
  }
  
  const getEventColor = (event) => {
    const eventLower = event.event?.toLowerCase() || '';
    if (eventLower.includes('complimentary') || eventLower.includes('free')) return '#9C27B0';
    if (eventLower.includes('discount')) return '#FF9800';
    if (eventLower.includes('payment')) return '#2E7D32';
    if (eventLower.includes('cancel')) return '#C62828';
    if (eventLower.includes('create') || eventLower.includes('order')) return '#1976D2';
    if (eventLower.includes('refund')) return '#4A148C';
    return theme.palette.primary.main;
  };
  
  const getEventIcon = (event) => {
    const eventLower = event.event?.toLowerCase() || '';
    if (eventLower.includes('complimentary') || eventLower.includes('free')) return <ComplimentaryIcon fontSize="small" />;
    if (eventLower.includes('discount')) return <DiscountIcon fontSize="small" />;
    if (eventLower.includes('payment')) return <PaymentIcon fontSize="small" />;
    if (eventLower.includes('cancel')) return <CancelIcon fontSize="small" />;
    if (eventLower.includes('refund')) return <ReceiptIcon fontSize="small" />;
    if (eventLower.includes('create') || eventLower.includes('order')) return <EventIcon fontSize="small" />;
    if (eventLower.includes('update') || eventLower.includes('change')) return <ScheduleIcon fontSize="small" />;
    return <HistoryIcon fontSize="small" />;
  };
  
  return (
    <TimelineContainer>
      {events.map((event, index) => {
        const isLast = index === events.length - 1;
        const color = getEventColor(event);
        
        return (
          <TimelineItemContainer key={index} isLast={isLast}>
            <TimelineDotWrapper>
              <TimelineDot color={color}>
                {getEventIcon(event)}
              </TimelineDot>
              <TimelineConnector isLast={isLast} />
            </TimelineDotWrapper>
            
            <TimelineContentWrapper>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: '12px',
                  bgcolor: alpha(color, 0.04),
                  border: `2px solid ${alpha(color, 0.12)}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: color }}>
                    {event.event || 'Event'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    {formatDateTime(event.time)}
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, color: 'text.secondary', mt: 0.5, fontWeight: 500 }}>
                  {event.description || 'No description available'}
                </Typography>
                
                {event.details && Object.keys(event.details).length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `2px solid ${alpha(color, 0.1)}` }}>
                    {Object.entries(event.details).map(([key, value]) => {
                      if (value === null || value === undefined) return null;
                      if (typeof value === 'number' || typeof value === 'string') {
                        if (typeof value === 'string' && value.length === 0) return null;
                        const displayValue = typeof value === 'number' ? formatCurrency(value) : value;
                        return (
                          <Typography 
                            key={key} 
                            variant="body2"
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
                              display: 'block', 
                              color: 'text.secondary',
                              fontWeight: 600,
                              py: 0.25,
                            }}
                          >
                            <strong style={{ fontWeight: 800 }}>{key.replace(/_/g, ' ').toUpperCase()}:</strong> {displayValue}
                          </Typography>
                        );
                      }
                      return null;
                    })}
                  </Box>
                )}
              </Paper>
            </TimelineContentWrapper>
          </TimelineItemContainer>
        );
      })}
    </TimelineContainer>
  );
});

CustomTimeline.displayName = 'CustomTimeline';

// ============================================
// CHILD COMPONENT: Transaction Detail View - FIXED
// ============================================

const TransactionDetailView = memo(({ transaction, onClose, open }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    items: true,
    payments: true,
    narrative: true,
    timeline: true,
    auditTrail: true,
  });
  
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);
  
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);
  
  // Determine payment method breakdown for this transaction
  const getPaymentMethodBreakdown = useCallback(() => {
    const breakdown = {};
    const payments = transaction?.payments || [];
    
    payments.forEach(p => {
      const method = p.payment_method || 'Unknown';
      if (!breakdown[method]) {
        breakdown[method] = { total: 0, count: 0 };
      }
      breakdown[method].total += p.amount || 0;
      breakdown[method].count += 1;
    });
    
    return breakdown;
  }, [transaction?.payments]);
  
  // NOW we can do the conditional return - AFTER all hooks
  if (!transaction) return null;
  
  const isCancelled = transaction.order_status === 'CANCELLED';
  const isComplimentary = transaction.is_complimentary || transaction.payment_status === 'COMPLIMENTARY';
  
  const orderStatusColor = getStatusColor(transaction.order_status);
  const paymentStatusColor = getStatusColor(transaction.payment_status);
  
  // Build audit trail from narrative
  const auditTrail = transaction.narrative?.timeline || [];
  
  const paymentBreakdown = getPaymentMethodBreakdown();
  const uniqueMethods = Object.keys(paymentBreakdown);
  const isMixedPayment = uniqueMethods.length > 1;
  const totalPaymentAmount = transaction.total_paid || 0;
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '95vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{
        borderBottom: `2px solid ${alpha('#000', 0.06)}`,
        pb: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        bgcolor: alpha('#1976D2', 0.04),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ReceiptLongIcon sx={{ color: isCancelled ? '#C62828' : '#1976D2', fontSize: { xs: 28, sm: 32 } }} />
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.4rem' } }}>
            {isCancelled ? 'Cancelled Transaction' : 'Transaction Details'}
          </Typography>
          <Chip
            label={transaction.order_number}
            size="medium"
            sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
          />
          {isComplimentary && (
            <Chip
              label="🆓 Complimentary"
              size="medium"
              sx={{
                bgcolor: '#F3E5F5',
                color: '#7B1FA2',
                fontWeight: 700,
                fontSize: { xs: '0.65rem', sm: '0.8rem' },
              }}
            />
          )}
          {isCancelled && (
            <Chip
              label="❌ Cancelled"
              size="medium"
              sx={{
                bgcolor: '#FFEBEE',
                color: '#C62828',
                fontWeight: 700,
                fontSize: { xs: '0.65rem', sm: '0.8rem' },
              }}
            />
          )}
          {isMixedPayment && (
            <Chip
              label="🔄 Mixed Payment"
              size="medium"
              sx={{
                bgcolor: '#FFF3E0',
                color: '#E65100',
                fontWeight: 700,
                fontSize: { xs: '0.65rem', sm: '0.8rem' },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton onClick={onClose} size="large">
            <CloseIcon fontSize={isMobile ? 'medium' : 'large'} />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: { xs: 2, sm: 4 }, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: '12px' }} />
            <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: '12px' }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '12px' }} />
          </Box>
        ) : (
          <>
            {/* Tabs for different views */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mb: 3,
                '& .MuiTab-root': {
                  fontWeight: 700,
                  fontSize: { xs: '0.7rem', sm: '0.85rem' },
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: { xs: 1.5, sm: 3 },
                },
                '& .Mui-selected': {
                  color: '#1976D2',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976D2',
                },
              }}
            >
              <Tab label="📋 Overview" iconPosition="start" />
              <Tab label="💰 Payment Breakdown" iconPosition="start" />
              <Tab label="📝 Narrative" iconPosition="start" />
              <Tab label="🔄 Timeline" iconPosition="start" />
              <Tab label="📦 Items" iconPosition="start" />
            </Tabs>

            {/* Tab 0: Overview */}
            {activeTab === 0 && (
              <>
                {/* Overview Section */}
                <Accordion
                  expanded={expandedSections.overview}
                  onChange={() => toggleSection('overview')}
                  elevation={0}
                  sx={{
                    border: `2px solid ${alpha('#000', 0.06)}`,
                    borderRadius: '14px !important',
                    mb: 2.5,
                    '&::before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      📋 Order Overview
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Order Number
                        </Typography>
                        <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                          {transaction.order_number}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Serial Number
                        </Typography>
                        <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                          #{String(transaction.serial_number || 0).padStart(6, '0')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Order Type
                        </Typography>
                        <Chip
                          label={transaction.order_type || 'Unknown'}
                          size="medium"
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.85rem' },
                            bgcolor: transaction.order_type === 'DINE_IN' ? '#E3F2FD' : '#FFF3E0',
                            color: transaction.order_type === 'DINE_IN' ? '#0D47A1' : '#E65100',
                            fontWeight: 700,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Location
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#1976D2' }} />
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                            {transaction.location_name}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Table
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TableRestaurantIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#FF6F00' }} />
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                            {transaction.table_name || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Waiter
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Avatar sx={{ width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 }, fontSize: { xs: '0.6rem', sm: '0.7rem' }, bgcolor: '#1976D2' }}>
                            {getInitials(transaction.waiter_name)}
                          </Avatar>
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                            {transaction.waiter_name}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Cashier
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#2E7D32' }} />
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                            {transaction.cashier_name || 'Not assigned'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Order Status
                        </Typography>
                        <Chip
                          size="medium"
                          label={orderStatusColor.label}
                          sx={{
                            bgcolor: orderStatusColor.bg,
                            color: orderStatusColor.color,
                            fontWeight: 700,
                            fontSize: { xs: '0.65rem', sm: '0.8rem' },
                            height: { xs: 28, sm: 32 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Payment Status
                        </Typography>
                        <Chip
                          size="medium"
                          label={paymentStatusColor.label}
                          sx={{
                            bgcolor: paymentStatusColor.bg,
                            color: paymentStatusColor.color,
                            fontWeight: 700,
                            fontSize: { xs: '0.65rem', sm: '0.8rem' },
                            height: { xs: 28, sm: 32 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Customer
                        </Typography>
                        <Box>
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                            {transaction.customer_name || 'Walk-in'}
                          </Typography>
                          {transaction.customer_phone && (
                            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, display: 'block' }}>
                              📱 {transaction.customer_phone}
                            </Typography>
                          )}
                          {transaction.customer_email && (
                            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, display: 'block' }}>
                              ✉️ {transaction.customer_email}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                          Order Date
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                          {formatDateTime(transaction.order_time)}
                        </Typography>
                      </Grid>
                      {/* Payment Method Summary */}
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, display: 'block', mb: 1 }}>
                          Payment Methods Used
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.entries(paymentBreakdown).map(([method, data]) => {
                            const color = getPaymentMethodColor(method);
                            const percentage = totalPaymentAmount > 0 ? (data.total / totalPaymentAmount) * 100 : 0;
                            return (
                              <Chip
                                key={method}
                                icon={getPaymentMethodIcon(method)}
                                label={`${method}: ${formatCurrency(data.total)} (${percentage.toFixed(1)}%)`}
                                sx={{
                                  bgcolor: alpha(color, 0.12),
                                  color: color,
                                  fontWeight: 700,
                                  fontSize: { xs: '0.6rem', sm: '0.75rem' },
                                  py: { xs: 1, sm: 1.5 },
                                  height: 'auto',
                                  '& .MuiChip-label': {
                                    px: 1,
                                  },
                                }}
                              />
                            );
                          })}
                          {isMixedPayment && (
                            <Chip
                              label="🔄 Mixed Payment Transaction"
                              sx={{
                                bgcolor: alpha('#E65100', 0.12),
                                color: '#E65100',
                                fontWeight: 700,
                                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Financial Summary */}
                <Accordion
                  expanded={expandedSections.overview}
                  elevation={0}
                  sx={{
                    border: `2px solid ${alpha('#000', 0.06)}`,
                    borderRadius: '14px !important',
                    mb: 2.5,
                    '&::before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => toggleSection('overview')}>
                    <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                      💰 Financial Summary
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#1976D2', 0.06), borderRadius: '10px' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Subtotal
                          </Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: '#1976D2' }}>
                            {formatCurrency(transaction.subtotal)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#FF9800', 0.06), borderRadius: '10px' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Discount
                          </Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: '#FF9800' }}>
                            {formatCurrency(transaction.discount)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#9C27B0', 0.06), borderRadius: '10px' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Tax
                          </Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: '#9C27B0' }}>
                            {formatCurrency(transaction.tax)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: isCancelled ? alpha('#C62828', 0.06) : alpha('#2E7D32', 0.06), borderRadius: '10px' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Total
                          </Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: isCancelled ? '#C62828' : '#2E7D32' }}>
                            {formatCurrency(transaction.total_amount)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#2E7D32', 0.06), borderRadius: '10px' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Paid
                          </Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: '#2E7D32' }}>
                            {formatCurrency(transaction.total_paid)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={4} md={2.4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: transaction.balance > 0 ? alpha('#C62828', 0.06) : alpha('#2E7D32', 0.06), borderRadius: '10px' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            Balance
                          </Typography>
                          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: transaction.balance > 0 ? '#C62828' : '#2E7D32' }}>
                            {formatCurrency(transaction.balance)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            {/* Tab 1: Payment Breakdown */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                  💳 Payment Breakdown
                </Typography>
                <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '14px', border: `2px solid ${alpha('#000', 0.06)}` }}>
                  <Grid container spacing={2}>
                    {Object.entries(paymentBreakdown).map(([method, data]) => {
                      const color = getPaymentMethodColor(method);
                      const percentage = totalPaymentAmount > 0 ? (data.total / totalPaymentAmount) * 100 : 0;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={method}>
                          <Paper sx={{ p: 2, borderRadius: '10px', bgcolor: alpha(color, 0.05), border: `2px solid ${alpha(color, 0.15)}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(color, 0.15), color: color }}>
                                {getPaymentMethodIcon(method)}
                              </Avatar>
                              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                {method}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                              <Typography variant="h6" fontWeight={800} sx={{ color: color, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                                {formatCurrency(data.total)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                {data.count} payment{data.count !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: alpha(color, 0.15) }}>
                              <Box sx={{ height: '100%', borderRadius: 3, bgcolor: color, width: `${Math.min(percentage, 100)}%` }} />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                              {percentage.toFixed(1)}% of total payment
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {isMixedPayment && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: '10px', fontWeight: 600 }}>
                      🔄 This transaction used <strong>multiple payment methods</strong> (mixed payment). Total: {formatCurrency(totalPaymentAmount)}
                    </Alert>
                  )}
                  {transaction.balance > 0 && transaction.payment_status !== 'COMPLIMENTARY' && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px', fontWeight: 600 }}>
                      ⚠️ Outstanding balance of {formatCurrency(transaction.balance)} remains unpaid.
                    </Alert>
                  )}
                </Paper>
              </Box>
            )}

            {/* Tab 2: Narrative */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                  📝 Transaction Narrative
                </Typography>
                <Paper 
                  sx={{ 
                    p: { xs: 3, sm: 4 }, 
                    bgcolor: alpha('#2E7D32', 0.04), 
                    borderRadius: '14px',
                    border: `2px solid ${alpha('#2E7D32', 0.1)}`,
                  }}
                >
                  <Typography 
                    variant="h6"
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1.1rem' }, 
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word',
                      lineHeight: 2,
                      fontWeight: 600,
                    }}
                  >
                    {transaction.narrative?.summary || 'No narrative available'}
                  </Typography>
                  
                  {transaction.narrative?.key_events && transaction.narrative.key_events.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, color: '#1976D2' }}>
                        🔑 Key Events
                      </Typography>
                      <List dense sx={{ bgcolor: alpha('#1976D2', 0.06), borderRadius: '10px', mt: 1 }}>
                        {transaction.narrative.key_events.map((event, idx) => (
                          <ListItem key={idx} sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <EventIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#1976D2' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={event} 
                              primaryTypographyProps={{ 
                                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                fontWeight: 600,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {transaction.narrative?.issues && transaction.narrative.issues.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" fontWeight={800} color="error" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                        ⚠️ Issues Detected
                      </Typography>
                      <List dense sx={{ bgcolor: alpha('#C62828', 0.06), borderRadius: '10px', mt: 1 }}>
                        {transaction.narrative.issues.map((issue, idx) => (
                          <ListItem key={idx} sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <ErrorIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#C62828' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={issue} 
                              primaryTypographyProps={{ 
                                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                color: '#C62828',
                                fontWeight: 700,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {transaction.narrative?.recommendations && transaction.narrative.recommendations.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" fontWeight={800} color="info.main" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                        💡 Recommendations
                      </Typography>
                      <List dense sx={{ bgcolor: alpha('#1976D2', 0.06), borderRadius: '10px', mt: 1 }}>
                        {transaction.narrative.recommendations.map((rec, idx) => (
                          <ListItem key={idx} sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <InfoIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#1976D2' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={rec} 
                              primaryTypographyProps={{ 
                                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                color: '#1976D2',
                                fontWeight: 600,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* Tab 3: Timeline */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                  🔄 Transaction Timeline
                </Typography>
                <CustomTimeline events={auditTrail} isLoading={isLoading} />
              </Box>
            )}

            {/* Tab 4: Items */}
            {activeTab === 4 && (
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                  📦 Order Items ({transaction.item_count || 0})
                </Typography>
                {transaction.items && transaction.items.length > 0 ? (
                  <TableContainer component={Paper} elevation={0} sx={{ border: `2px solid ${alpha('#000', 0.06)}`, borderRadius: '10px' }}>
                    <Table size="medium">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha('#1976D2', 0.06) }}>
                          <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Item</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Category</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Qty</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Price</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transaction.items.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 600 }}>
                              {item.name}
                              {item.notes && (
                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, display: 'block' }}>
                                  📝 {item.notes}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, color: 'text.secondary', fontWeight: 500 }}>
                              {item.category}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 600 }}>
                              {item.quantity}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 600 }}>
                              {formatCurrency(item.price)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 700 }}>
                              {formatCurrency(item.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: '10px', fontSize: '1rem', fontWeight: 600 }}>
                    No items found for this order.
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{
        borderTop: `2px solid ${alpha('#000', 0.06)}`,
        pt: 2.5,
        px: { xs: 2, sm: 4 },
        pb: { xs: 2, sm: 4 },
        gap: 1.5,
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '10px',
            px: { xs: 3, sm: 4 },
            py: 1.25,
            fontSize: { xs: '0.85rem', sm: '1rem' },
            bgcolor: '#1976D2',
            '&:hover': { bgcolor: '#1565C0' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

TransactionDetailView.displayName = 'TransactionDetailView';

// ============================================
// MAIN COMPONENT
// ============================================

const TransactionDetails = ({ locationKey = 'all' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get location config
  const locationConfig = LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.all;
  const LOCATION_COLOR = locationConfig.color || '#1976D2';
  const LOCATION_NAME = locationConfig.name || 'All Locations';
  const LOCATION_ID = locationConfig.id;
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(null);
  
  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [locationId, setLocationId] = useState(LOCATION_ID || '');
  const [orderStatus, setOrderStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(true);
  const [includeComplimentary, setIncludeComplimentary] = useState(true);
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState(0);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // ============================================
  // REFS
  // ============================================
  
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  
  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchTransactions = useCallback(async (skipLoading = false) => {
    try {
      console.debug(`[TransactionDetails] Fetching transactions for ${LOCATION_NAME}...`);
      if (!skipLoading) {
        setIsFetching(true);
      }
      setError(null);
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const params = {
        period,
        page,
        page_size: pageSize,
        include_cancelled: includeCancelled,
        include_complimentary: includeComplimentary,
      };
      
      if (locationId) params.location_id = parseInt(locationId);
      if (orderStatus) params.order_status = orderStatus;
      if (paymentStatus) params.payment_status = paymentStatus;
      if (searchTerm) params.search = searchTerm;
      
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      
      console.debug('[TransactionDetails] Request params:', params);
      const data = await apiRequest('/account/transactions/detailed', { params });
      console.debug('[TransactionDetails] Transactions fetched:', data);
      
      if (isMountedRef.current) {
        setTransactions(data.transactions || []);
        setTotalCount(data.total_count || 0);
        setSummary(data.summary || null);
        setFilters(data.filters || null);
      }
      
      return data;
    } catch (err) {
      if (err.name === 'AbortError') {
        console.debug('[TransactionDetails] Request aborted');
        return null;
      }
      console.error('[TransactionDetails] Error fetching transactions:', err);
      setError(err.message);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch transactions: ${err.message}`,
          severity: 'error',
        });
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
        if (!skipLoading) {
          setIsLoading(false);
        }
      }
    }
  }, [period, startDate, endDate, locationId, orderStatus, paymentStatus, searchTerm, includeCancelled, includeComplimentary, page, pageSize, LOCATION_NAME]);
  
  const fetchTransactionDetail = useCallback(async (transactionId) => {
    try {
      console.debug(`[TransactionDetails] Fetching transaction detail for ID: ${transactionId}`);
      const data = await apiRequest(`/account/transactions/${transactionId}/detail`);
      console.debug('[TransactionDetails] Transaction detail fetched:', data);
      return data;
    } catch (err) {
      console.error('[TransactionDetails] Error fetching transaction detail:', err);
      setSnackbar({
        open: true,
        message: `Failed to fetch transaction detail: ${err.message}`,
        severity: 'error',
      });
      return null;
    }
  }, []);
  
  // ============================================
  // EFFECTS
  // ============================================
  
  useEffect(() => {
    isMountedRef.current = true;
    
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
    
    if (LOCATION_ID) {
      setLocationId(String(LOCATION_ID));
    }
    
    initialLoadDoneRef.current = false;
    fetchTransactions().then(() => {
      initialLoadDoneRef.current = true;
    });
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [LOCATION_ID]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Auto-fetch when filters change (debounced)
  useEffect(() => {
    if (initialLoadDoneRef.current) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        fetchTransactions(true);
      }, 400);
    }
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [period, locationId, orderStatus, paymentStatus, searchTerm, includeCancelled, includeComplimentary, page, pageSize, fetchTransactions]);
  
  // ============================================
  // HANDLERS
  // ============================================
  
  const handleRefresh = useCallback(async () => {
    await fetchTransactions();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully!',
      severity: 'success',
    });
  }, [fetchTransactions]);
  
  const handlePeriodChange = useCallback((event) => {
    const newPeriod = event.target.value;
    setPeriod(newPeriod);
    setShowDatePicker(newPeriod === 'custom');
    setPage(1);
  }, []);
  
  const handleApplyCustomDate = useCallback(() => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setSnackbar({
          open: true,
          message: 'Start date must be before end date',
          severity: 'warning',
        });
        return;
      }
      setPage(1);
      fetchTransactions();
      setShowDatePicker(false);
    } else {
      setSnackbar({
        open: true,
        message: 'Please select both start and end dates',
        severity: 'warning',
      });
    }
  }, [startDate, endDate, fetchTransactions]);
  
  const handleViewDetail = useCallback(async (transaction) => {
    console.debug('[TransactionDetails] Viewing detail for:', transaction.order_number);
    
    if (transaction.narrative && transaction.narrative.summary) {
      setSelectedTransaction(transaction);
      setDetailDialogOpen(true);
    } else {
      const detail = await fetchTransactionDetail(transaction.id);
      if (detail) {
        setSelectedTransaction({
          ...transaction,
          ...detail,
          narrative: detail.narrative || transaction.narrative,
        });
        setDetailDialogOpen(true);
      }
    }
  }, [fetchTransactionDetail]);
  
  const handleCloseDetail = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedTransaction(null);
  }, []);
  
  const handleExport = useCallback(async () => {
    try {
      console.debug('[TransactionDetails] Exporting data...');
      setSnackbar({
        open: true,
        message: 'Export functionality coming soon!',
        severity: 'info',
      });
    } catch (err) {
      console.error('[TransactionDetails] Error exporting:', err);
      setSnackbar({
        open: true,
        message: `Export failed: ${err.message}`,
        severity: 'error',
      });
    }
  }, []);
  
  const handleMainTabChange = useCallback((event, newValue) => {
    setActiveMainTab(newValue);
  }, []);
  
  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  
  const renderFilters = useCallback(() => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: '16px',
          border: `2px solid ${alpha(LOCATION_COLOR, 0.1)}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 600 }}>Period</InputLabel>
              <Select
                value={period}
                onChange={handlePeriodChange}
                label="Period"
                sx={{
                  borderRadius: '12px',
                  fontSize: { xs: '0.8rem', sm: '0.95rem' },
                  fontWeight: 600,
                }}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 600 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {showDatePicker && (
            <Grid item xs={12} sm={6} md={5}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: `2px solid ${alpha('#000', 0.15)}`,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    background: 'white',
                    flex: 1,
                    minWidth: '120px',
                  }}
                />
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#666' }}>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: `2px solid ${alpha('#000', 0.15)}`,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    background: 'white',
                    flex: 1,
                    minWidth: '120px',
                  }}
                />
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleApplyCustomDate}
                  sx={{
                    bgcolor: LOCATION_COLOR,
                    '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.85rem' },
                    borderRadius: '10px',
                    px: 3,
                    py: 1,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 600 }}>Order Status</InputLabel>
              <Select
                value={orderStatus}
                onChange={(e) => { setOrderStatus(e.target.value); setPage(1); }}
                label="Order Status"
                sx={{
                  borderRadius: '12px',
                  fontSize: { xs: '0.8rem', sm: '0.95rem' },
                  fontWeight: 600,
                }}
              >
                {ORDER_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 600 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 600 }}>Payment Status</InputLabel>
              <Select
                value={paymentStatus}
                onChange={(e) => { setPaymentStatus(e.target.value); setPage(1); }}
                label="Payment Status"
                sx={{
                  borderRadius: '12px',
                  fontSize: { xs: '0.8rem', sm: '0.95rem' },
                  fontWeight: 600,
                }}
              >
                {PAYMENT_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 600 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search by order #, customer, phone, email..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontSize: { xs: '0.8rem', sm: '0.95rem' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 24, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="medium" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="medium" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600 }}>Cancelled</InputLabel>
              <Select
                value={includeCancelled ? 'yes' : 'no'}
                onChange={(e) => { setIncludeCancelled(e.target.value === 'yes'); setPage(1); }}
                label="Cancelled"
                sx={{
                  borderRadius: '12px',
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  fontWeight: 600,
                }}
              >
                <MenuItem value="yes" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, fontWeight: 600 }}>Include</MenuItem>
                <MenuItem value="no" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, fontWeight: 600 }}>Exclude</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="medium">
              <InputLabel sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600 }}>Complimentary</InputLabel>
              <Select
                value={includeComplimentary ? 'yes' : 'no'}
                onChange={(e) => { setIncludeComplimentary(e.target.value === 'yes'); setPage(1); }}
                label="Complimentary"
                sx={{
                  borderRadius: '12px',
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  fontWeight: 600,
                }}
              >
                <MenuItem value="yes" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, fontWeight: 600 }}>Include</MenuItem>
                <MenuItem value="no" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, fontWeight: 600 }}>Exclude</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              size="medium"
              onClick={handleExport}
              startIcon={<DownloadIcon />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                borderColor: LOCATION_COLOR,
                color: LOCATION_COLOR,
                py: 1,
                '&:hover': {
                  borderColor: LOCATION_COLOR,
                  bgcolor: alpha(LOCATION_COLOR, 0.08),
                },
              }}
            >
              Export
            </Button>
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={handleRefresh}
              startIcon={isFetching ? <CircularProgress size={22} color="inherit" /> : <RefreshIcon />}
              sx={{
                bgcolor: LOCATION_COLOR,
                '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                borderRadius: '12px',
                py: 1,
              }}
              disabled={isFetching}
            >
              {isFetching ? 'Loading...' : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  }, [period, startDate, endDate, orderStatus, paymentStatus, searchTerm, includeCancelled, includeComplimentary, isFetching, showDatePicker, LOCATION_COLOR, handlePeriodChange, handleApplyCustomDate, handleExport, handleRefresh]);
  
  const renderSummary = useCallback(() => {
    if (!summary) return null;
    
    const cards = [
      { label: 'Total Revenue', value: formatCurrency(summary.total_revenue), color: '#2E7D32' },
      { label: 'Total Orders', value: summary.total_orders || 0, color: LOCATION_COLOR },
      { label: 'Total Discounts', value: formatCurrency(summary.total_discounts), color: '#FF9800' },
      { label: 'Total Complimentary', value: formatCurrency(summary.total_complimentary), color: '#9C27B0' },
      { label: 'Average Order', value: formatCurrency(summary.average_order), color: '#E65100' },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={6} sm={4} md={2.4} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: '14px',
                border: `2px solid ${alpha(card.color, 0.15)}`,
                bgcolor: alpha(card.color, 0.05),
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {card.label}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' },
                color: card.color,
              }}>
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }, [summary, LOCATION_COLOR]);
  
  const renderTransactionRow = useCallback((transaction) => {
    const isCancelled = transaction.order_status === 'CANCELLED';
    const isComplimentary = transaction.is_complimentary || transaction.payment_status === 'COMPLIMENTARY';
    const hasNarrative = !!(transaction.narrative && transaction.narrative.summary);
    
    const orderColor = getStatusColor(transaction.order_status);
    const paymentColor = getStatusColor(transaction.payment_status);
    
    // Determine payment method display
    const paymentMethods = transaction.payments?.map(p => p.payment_method) || [];
    const uniqueMethods = [...new Set(paymentMethods)];
    const displayMethod = uniqueMethods.length > 1 ? 'Mixed' : (uniqueMethods[0] || 'Unpaid');
    const methodColor = getPaymentMethodColor(displayMethod);
    
    return (
      <TableRow 
        key={transaction.id} 
        hover
        sx={{
          opacity: isCancelled ? 0.7 : 1,
          '&:hover': {
            bgcolor: alpha(LOCATION_COLOR, 0.04),
          },
          cursor: 'pointer',
        }}
        onClick={() => handleViewDetail(transaction)}
      >
        <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 700 }}>
          {transaction.order_number}
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.6rem', display: 'block' }}>
            #{String(transaction.serial_number || 0).padStart(6, '0')}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 500 }}>
          {formatDateTime(transaction.order_time)}
        </TableCell>
        <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 600 }}>
          {transaction.location_name}
        </TableCell>
        <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 500 }}>
          {transaction.customer_name || 'Walk-in'}
          {transaction.customer_phone && (
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.6rem', display: 'block' }}>
              📱 {transaction.customer_phone}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 500 }}>
          {transaction.waiter_name}
        </TableCell>
        <TableCell>
          <Chip
            size="medium"
            label={orderColor.label}
            sx={{
              bgcolor: orderColor.bg,
              color: orderColor.color,
              fontWeight: 700,
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              height: { xs: 24, sm: 28 },
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="medium"
            label={paymentColor.label}
            sx={{
              bgcolor: paymentColor.bg,
              color: paymentColor.color,
              fontWeight: 700,
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              height: { xs: 24, sm: 28 },
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="medium"
            label={displayMethod}
            sx={{
              bgcolor: alpha(methodColor, 0.12),
              color: methodColor,
              fontWeight: 700,
              fontSize: { xs: '0.55rem', sm: '0.7rem' },
              height: { xs: 22, sm: 26 },
            }}
          />
        </TableCell>
        <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 700 }}>
          {isCancelled ? (
            <Chip label="CANCELLED" size="medium" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 700, fontSize: '0.6rem' }} />
          ) : isComplimentary ? (
            <Chip label="🆓 FREE" size="medium" sx={{ bgcolor: '#F3E5F5', color: '#7B1FA2', fontWeight: 700, fontSize: '0.6rem' }} />
          ) : (
            formatCurrency(transaction.total_amount)
          )}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 700 }}>
          {formatCurrency(transaction.total_paid)}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasNarrative && (
              <Tooltip title="Has narrative details">
                <DescriptionIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
              </Tooltip>
            )}
            {uniqueMethods.length > 1 && (
              <Tooltip title="Mixed payment transaction">
                <PointOfSaleIcon sx={{ fontSize: 18, color: '#E65100' }} />
              </Tooltip>
            )}
            <Tooltip title="View Details">
              <IconButton
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetail(transaction);
                }}
                sx={{
                  color: LOCATION_COLOR,
                  '&:hover': { bgcolor: alpha(LOCATION_COLOR, 0.1) },
                }}
              >
                <VisibilityIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  }, [handleViewDetail, LOCATION_COLOR]);
  
  // ============================================
  // EARLY RETURN - After all hooks
  // ============================================
  if (isLoading) {
    const config = LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.all;
    return <Preloader text={`Loading ${config.name} Transactions...`} size={70} fullScreen />;
  }
  
  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar locationKey={locationKey} locationColor={LOCATION_COLOR} />
        
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <Header />
          
          <Box sx={{ 
            flex: 1, 
            p: { xs: 1.5, sm: 2, md: 3 },
            overflow: 'auto',
            bgcolor: '#f5f7fa',
          }}>
            {/* Header Paper */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                mb: { xs: 2, sm: 3 },
                borderRadius: '18px',
                border: `2px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexWrap: 'wrap',
                gap: 2,
                bgcolor: 'white',
              }}
            >
              <Box>
                <Typography variant="h4" sx={{
                  color: LOCATION_COLOR,
                  fontWeight: 900,
                  fontSize: { xs: '1.4rem', sm: '2rem', md: '2.4rem' },
                }}>
                  {LOCATION_CONFIG[locationKey]?.icon || '📋'} {LOCATION_NAME} Transactions
                  <span style={{
                    display: 'inline-block',
                    width: '4px',
                    height: { xs: '28px', sm: '36px' },
                    backgroundColor: LOCATION_COLOR,
                    marginLeft: '8px',
                    verticalAlign: 'text-bottom',
                    animation: 'blink 1s step-end infinite',
                  }} />
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 600 }}>
                  {totalCount} transactions found • Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}
                  {LOCATION_ID && ` • Filtered by ${LOCATION_NAME}`}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleRefresh}
                  startIcon={isFetching ? <CircularProgress size={22} color="inherit" /> : <RefreshIcon />}
                  sx={{
                    bgcolor: LOCATION_COLOR,
                    '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    borderRadius: '12px',
                    px: 3,
                    py: 1.25,
                  }}
                  disabled={isFetching}
                >
                  {isFetching ? 'Loading...' : 'Refresh'}
                </Button>
              </Box>
            </Paper>
            
            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: '12px', fontSize: '1rem', fontWeight: 600 }}
                action={
                  <Button color="inherit" size="medium" onClick={handleRefresh} sx={{ fontWeight: 700 }}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            )}
            
            {/* Summary */}
            {renderSummary()}
            
            {/* Filters */}
            {renderFilters()}
            
            {/* Main Tabs: Transactions | Sales Summary */}
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={activeMainTab}
                onChange={handleMainTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.95rem' },
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: { xs: 2, sm: 4 },
                    py: 1.5,
                  },
                  '& .Mui-selected': {
                    color: LOCATION_COLOR,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: LOCATION_COLOR,
                  },
                }}
              >
                <Tab label="📋 Transactions" iconPosition="start" />
                <Tab label="💰 Sales Summary" iconPosition="start" />
              </Tabs>
            </Box>
            
            {/* Tab 0: Transactions Table */}
            {activeMainTab === 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '18px',
                  border: `2px solid ${alpha('#000', 0.06)}`,
                  overflow: 'hidden',
                  bgcolor: 'white',
                }}
              >
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(LOCATION_COLOR, 0.06) }}>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Order #</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Waiter</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Order Status</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Payment</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Method</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Paid</TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.85rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                            <ReceiptIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 700 }}>
                              No transactions found
                            </Typography>
                            <Typography variant="body1" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.9rem', display: 'block', mt: 1 }}>
                              {totalCount > 0 ? 'Data processing issue. Try refreshing or check backend logs.' : 'Try adjusting your filters or search terms'}
                            </Typography>
                            {totalCount > 0 && (
                              <Button
                                size="medium"
                                variant="outlined"
                                onClick={handleRefresh}
                                sx={{ mt: 3, fontWeight: 700 }}
                              >
                                Refresh Data
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactions.map(renderTransactionRow)
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {totalCount > pageSize && (
                  <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page - 1}
                      onPageChange={(_, newPage) => setPage(newPage + 1)}
                      rowsPerPage={pageSize}
                      onRowsPerPageChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setPage(1);
                      }}
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      sx={{
                        '& .MuiTablePagination-select': {
                          fontSize: { xs: '0.8rem', sm: '0.95rem' },
                          fontWeight: 600,
                        },
                        '& .MuiTablePagination-displayedRows': {
                          fontSize: { xs: '0.75rem', sm: '0.9rem' },
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Box>
                )}
              </Paper>
            )}
            
            {/* Tab 1: Sales Summary */}
            {activeMainTab === 1 && (
              <SalesSummaryTab 
                transactions={transactions} 
                summary={summary} 
                isLoading={isFetching} 
              />
            )}
            
            {/* Transaction Detail Dialog */}
            <TransactionDetailView
              open={detailDialogOpen}
              transaction={selectedTransaction}
              onClose={handleCloseDetail}
            />
            
            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={5000}
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              TransitionComponent={Fade}
            >
              <Alert
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                variant="filled"
                sx={{ width: '100%', borderRadius: '12px', fontSize: '1rem', fontWeight: 600 }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default TransactionDetails;